import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { DifficultyLevel, PronunciationStrictness, LanguageCode } from '../../../shared/types'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { LanguageSelector } from '../components/language/LanguageSelector'
import { useAvailableLanguages, useSwitchLanguage } from '../hooks/useLanguage'
import { useUserStore } from '../store/userStore'
import { updateUserProfile } from '../services/api'
import { AI_PERSONALITY_OPTIONS } from '../constants/aiPersonalities'

const difficultyOptions: Array<{
  id: DifficultyLevel
  title: string
  description: string
}> = [
  { id: 'beginner', title: 'Beginner', description: 'Learn foundational phrases, pronunciation, and scripts.' },
  { id: 'intermediate', title: 'Intermediate', description: 'Handle day-to-day conversations with confidence.' },
  { id: 'advanced', title: 'Advanced', description: 'Fine-tune fluency, nuance, and native-level responses.' },
]

const pronunciationOptions: Array<{
  id: PronunciationStrictness
  title: string
  description: string
}> = [
  { id: 'relaxed', title: 'Relaxed', description: 'Only major pronunciation issues are flagged.' },
  { id: 'moderate', title: 'Balanced', description: 'Helpful corrections without interrupting flow.' },
  { id: 'strict', title: 'Strict', description: 'Detailed coaching for near-native pronunciation.' },
]

const steps = [
  { id: 'language', title: 'Choose your language', description: 'Pick the language you want to focus on first.' },
  { id: 'difficulty', title: 'Match your level', description: 'Let the AI know how much help you need.' },
  {
    id: 'pronunciation',
    title: 'Pronunciation feedback',
    description: 'Decide how strict the corrections should be.',
  },
  {
    id: 'daily-target',
    title: 'Daily review target',
    description: 'Set the number of flashcards you want to review per day.',
  },
  {
    id: 'personality',
    title: 'Pick your AI coach',
    description: 'Choose the conversation style that will keep you motivated.',
  },
]

export function Onboarding() {
  const navigate = useNavigate()
  const {
    user,
    updateUser,
    aiPersonality,
    setAiPersonality,
    hasCompletedOnboarding,
    setOnboardingComplete,
  } = useUserStore()
  const { languages, isLoading: languagesLoading, error: languagesError } = useAvailableLanguages()
  const { switchLanguage, isLoading: switchingLanguage } = useSwitchLanguage()

  const [currentStep, setCurrentStep] = useState(0)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode | null>(null)
  const [difficultyLevel, setDifficultyLevel] = useState<DifficultyLevel>('beginner')
  const [pronunciationStrictness, setPronunciationStrictness] = useState<PronunciationStrictness>('moderate')
  const [dailyReviewTarget, setDailyReviewTarget] = useState<number>(20)
  const [selectedPersonality, setSelectedPersonality] = useState<string>(aiPersonality || 'friendly_tutor')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isFinalStep = currentStep === steps.length - 1
  const activeStep = steps[currentStep]

  useEffect(() => {
    if (!user) {
      navigate('/auth', { replace: true })
      return
    }

    if (user.targetLanguage) {
      setSelectedLanguage((user.targetLanguage as LanguageCode) || null)
    }
    if (user.difficultyLevel) {
      setDifficultyLevel(user.difficultyLevel as DifficultyLevel)
    }
    if (user.pronunciationStrictness) {
      setPronunciationStrictness(user.pronunciationStrictness as PronunciationStrictness)
    }
    if (typeof user.dailyReviewTarget === 'number') {
      setDailyReviewTarget(user.dailyReviewTarget)
    }
  }, [user])

  useEffect(() => {
    if (user && hasCompletedOnboarding) {
      navigate('/pricing', { replace: true })
    }
  }, [hasCompletedOnboarding, user, navigate])

  const progressPercent = useMemo(() => ((currentStep + 1) / steps.length) * 100, [currentStep])

  const canProceed = useMemo(() => {
    switch (steps[currentStep].id) {
      case 'language':
        return Boolean(selectedLanguage)
      case 'difficulty':
        return Boolean(difficultyLevel)
      case 'pronunciation':
        return Boolean(pronunciationStrictness)
      case 'daily-target':
        return dailyReviewTarget > 0
      case 'personality':
        return Boolean(selectedPersonality)
      default:
        return false
    }
  }, [currentStep, selectedLanguage, difficultyLevel, pronunciationStrictness, dailyReviewTarget, selectedPersonality])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSubmit = async () => {
    if (!user || !selectedLanguage) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      if (user.targetLanguage !== selectedLanguage) {
        await switchLanguage(selectedLanguage)
      }

      setAiPersonality(selectedPersonality)
      const response = await updateUserProfile(user.id, {
        difficultyLevel,
        dailyReviewTarget,
        pronunciationStrictness,
      })

      updateUser({
        ...response.user,
        hasCompletedOnboarding: true,
      })
      setOnboardingComplete()
      navigate('/pricing')
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save your preferences. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderLanguageStep = () => (
    <div>
      <LanguageSelector
        languages={languages}
        selectedLanguage={selectedLanguage}
        onSelect={(code) => setSelectedLanguage(code)}
        isLoading={languagesLoading}
        showDescriptions
      />
      {!languagesLoading && languagesError && (
        <p className="mt-4 text-sm text-error-600">Unable to load languages right now. Please refresh and try again.</p>
      )}
    </div>
  )

  const renderDifficultyStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {difficultyOptions.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setDifficultyLevel(option.id)}
          className={`p-4 rounded-xl border-2 text-left transition ${
            difficultyLevel === option.id
              ? 'border-primary-500 bg-primary-50 shadow-sm'
              : 'border-[var(--color-border)] hover:border-primary-300 bg-[var(--color-surface)]'
          }`}
        >
          <h3 className="text-lg font-semibold text-[var(--color-text)]">{option.title}</h3>
          <p className="mt-2 text-sm text-[var(--color-muted)]">{option.description}</p>
        </button>
      ))}
    </div>
  )

  const renderPronunciationStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {pronunciationOptions.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setPronunciationStrictness(option.id)}
          className={`p-4 rounded-xl border-2 text-left transition ${
            pronunciationStrictness === option.id
              ? 'border-primary-500 bg-primary-50 shadow-sm'
              : 'border-[var(--color-border)] hover:border-primary-300 bg-[var(--color-surface)]'
          }`}
        >
          <h3 className="text-lg font-semibold text-[var(--color-text)]">{option.title}</h3>
          <p className="mt-2 text-sm text-[var(--color-muted)]">{option.description}</p>
        </button>
      ))}
    </div>
  )

  const renderDailyTargetStep = () => (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Daily flashcards</p>
          <p className="text-4xl font-bold text-gray-900">{dailyReviewTarget}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Recommended</p>
          <p className="text-lg font-semibold text-[var(--color-text)]">15-25 cards</p>
        </div>
      </div>
      <input
        type="range"
        min={5}
        max={50}
        step={5}
        value={dailyReviewTarget}
        onChange={(e) => setDailyReviewTarget(Number(e.target.value))}
        className="w-full mt-6 accent-primary-600"
      />
      <div className="flex justify-between text-sm text-gray-500 mt-2">
        <span>Light</span>
        <span>Focused</span>
        <span>Intense</span>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        {[10, 15, 20, 25, 30].map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => setDailyReviewTarget(preset)}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition ${
              dailyReviewTarget === preset
                ? 'border-primary-500 text-primary-700 bg-primary-50'
                : 'border-gray-200 text-gray-700 hover:border-primary-300'
            }`}
          >
            {preset} / day
          </button>
        ))}
      </div>
    </div>
  )

  const renderPersonalityStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {AI_PERSONALITY_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setSelectedPersonality(option.id)}
          className={`p-5 rounded-xl border-2 text-left transition ${
            selectedPersonality === option.id
              ? 'border-primary-500 bg-primary-50 shadow-sm'
              : 'border-[var(--color-border)] hover:border-primary-300 bg-[var(--color-surface)]'
          }`}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[var(--color-text)]">{option.title}</h3>
            {selectedPersonality === option.id && (
              <span className="text-primary-600 text-sm font-medium">Selected</span>
            )}
          </div>
          <p className="mt-2 text-sm text-[var(--color-muted)]">{option.description}</p>
        </button>
      ))}
      <div className="bg-primary-600 text-white rounded-2xl p-6 md:col-span-2">
        <h4 className="text-lg font-semibold">You're almost ready!</h4>
        <p className="mt-2 text-sm opacity-90">
          We’ll use your preferences to tailor conversations, flashcards, and pronunciation coaching.
          You can tweak any of these settings later in your profile.
        </p>
      </div>
    </div>
  )

  const renderStepContent = () => {
    if (languagesLoading && currentStep === 0) {
      return (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )
    }

    switch (activeStep.id) {
      case 'language':
        return renderLanguageStep()
      case 'difficulty':
        return renderDifficultyStep()
      case 'pronunciation':
        return renderPronunciationStep()
      case 'daily-target':
        return renderDailyTargetStep()
      case 'personality':
        return renderPersonalityStep()
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-[var(--color-bg)] to-secondary-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-10">
          <p className="text-sm uppercase font-medium text-primary-500 tracking-wide">Quick setup</p>
          <h1 className="text-4xl font-semibold text-[var(--color-text)] mt-2">Personalize your learning experience</h1>
          <p className="text-[var(--color-muted)] mt-3 max-w-3xl">
            Answer a few questions so we can tailor lessons, pronunciation feedback, and daily goals to your needs.
            This takes less than a minute.
          </p>
        </div>

        <div className="bg-[var(--color-surface)] rounded-2xl shadow-lg border border-[var(--color-border-subtle)] p-6 md:p-10">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary-500">
                  Step {currentStep + 1} of {steps.length}
                </p>
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mt-1">{activeStep.title}</h2>
                <p className="text-[var(--color-muted)] mt-1">{activeStep.description}</p>
              </div>
              <p className="text-sm text-[var(--color-muted)]">{Math.round(progressPercent)}% complete</p>
            </div>
            <div className="mt-4 h-2 bg-[var(--color-surface-secondary)] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="space-y-6">{renderStepContent()}</div>

          {submitError && (
            <div className="mt-6 p-4 rounded-xl border border-error-200 bg-error-50 text-error-700 text-sm">
              {submitError}
            </div>
          )}

          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              {!isFinalStep && (
                <Button onClick={handleNext} disabled={!canProceed}>
                  Next
                </Button>
              )}
            </div>
            {isFinalStep && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={!canProceed || isSubmitting || switchingLanguage}
                isLoading={isSubmitting || switchingLanguage}
              >
                Save preferences
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-[var(--color-muted)] mt-8">
          Need to tweak something later? You can always adjust these preferences in your profile.
        </p>
      </div>
    </div>
  )
}

export default Onboarding
